import { useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  FRAME_FADE_MS,
  PANORAMA_FOV,
  prefersReducedMotion,
  zoomFov,
} from "utils/viewerInput";

const DEG = Math.PI / 180;
const vertex = `attribute vec2 position; varying vec2 uv; void main(){uv=position*.5+.5;gl_Position=vec4(position,0.,1.);}`;
// Two frames are bound at once: the current capture (texA/texB) and the previous one
// (prevA/prevB), so a new capture cross-fades in as `fade` goes from 0 to 1. Calibration is per
// lens and shared by both frames (centre as a fraction of width/height, radius as a fraction of
// the shorter side); sizes are per frame, since a frame can fall back to a smaller thumbnail.
const fragment = `precision highp float;
varying vec2 uv; uniform sampler2D texA,texB,prevA,prevB; uniform vec2 sizeA,sizeB,prevSizeA,prevSizeB,has,prevHas;
uniform vec4 calA,calB; uniform vec3 optA,optB; uniform float yawA,yawB,yaw,pitch,fov,aspect,seam,fade;
vec3 rotateY(vec3 v,float a){float s=sin(a),c=cos(a);return vec3(c*v.x+s*v.z,v.y,-s*v.x+c*v.z);}
vec4 sampleEye(sampler2D tex,vec2 size,vec4 cal,vec3 opt,vec3 ray,float available){
 if(available<.5)return vec4(0.);float theta=acos(clamp(ray.z,-1.,1.));if(theta>cal.w)return vec4(0.);
 float phi=atan(ray.y,ray.x)+opt.x;float r=cal.z*min(size.x,size.y)*theta/cal.w;
 vec2 px=vec2(cal.x*size.x+opt.y*r*cos(phi),cal.y*size.y-opt.z*r*sin(phi));vec2 edge=min(px,size-px);
 float w=clamp(min(edge.x,edge.y)/6.,0.,1.)*(1.-smoothstep(cal.w-max(seam,.0026),cal.w,theta));
 if(w<=0.)return vec4(0.);return vec4(texture2D(tex,px/size).rgb,w);
}
vec3 blend(vec4 a,vec4 b,float stripe){float w=a.a+b.a;
 return w<.0001?mix(vec3(.055,.075,.09),vec3(.085,.105,.12),stripe):(a.rgb*a.a+b.rgb*b.a)/w;}
void main(){float t=tan(fov*.5);vec2 sp=(uv*2.-1.)*vec2(aspect*t,t);
 vec3 forward=vec3(cos(pitch)*sin(yaw),sin(pitch),cos(pitch)*cos(yaw));vec3 right=vec3(cos(yaw),0.,-sin(yaw));
 vec3 ray=normalize(forward+right*sp.x+cross(forward,right)*sp.y);
 vec3 rayA=rotateY(ray,-yawA);vec3 rayB=rotateY(ray,-yawB);
 float stripe=mod(floor((gl_FragCoord.x+gl_FragCoord.y)/9.),2.);
 vec3 col=blend(sampleEye(texA,sizeA,calA,optA,rayA,has.x),sampleEye(texB,sizeB,calB,optB,rayB,has.y),stripe);
 if(fade<1.)col=mix(blend(sampleEye(prevA,prevSizeA,calA,optA,rayA,prevHas.x),sampleEye(prevB,prevSizeB,calB,optB,rayB,prevHas.y),stripe),col,fade);
 gl_FragColor=vec4(col,1.);}`;
const UNIFORMS = [
  "texA", "texB", "prevA", "prevB", "sizeA", "sizeB", "prevSizeA", "prevSizeB",
  "has", "prevHas", "calA", "calB", "optA", "optB", "yawA", "yawB",
  "yaw", "pitch", "fov", "aspect", "seam", "fade",
];
const clampFov = (fov) =>
  Math.max(PANORAMA_FOV.min, Math.min(PANORAMA_FOV.max, fov));

export default function Panorama({
  images,
  calibration,
  resetKey,
  viewRef,
  describedBy = "panorama-instructions",
}) {
  const canvas = useRef(null);
  const state = useRef(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const el = canvas.current;
    const gl = el.getContext("webgl", { alpha: false, antialias: false });
    if (!gl) {
      setError(
        "360 view needs WebGL. You can still inspect Camera A and Camera B.",
      );
      return;
    }
    let resize;
    const resources = { textures: [], shaders: [] };
    try {
      const shader = (type, source) => {
        const s = gl.createShader(type);
        resources.shaders.push(s);
        gl.shaderSource(s, source);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS))
          throw new Error("Unable to prepare the panorama renderer.");
        return s;
      };
      const program = gl.createProgram();
      resources.program = program;
      gl.attachShader(program, shader(gl.VERTEX_SHADER, vertex));
      gl.attachShader(program, shader(gl.FRAGMENT_SHADER, fragment));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS))
        throw new Error("Unable to start the panorama renderer.");
      gl.useProgram(program);
      const buffer = gl.createBuffer();
      resources.buffer = buffer;
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
        gl.STATIC_DRAW,
      );
      const position = gl.getAttribLocation(program, "position");
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      // Two frames x two lenses.
      for (let i = 0; i < 4; i++) {
        const tex = gl.createTexture();
        resources.textures.push(tex);
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texImage2D(
          gl.TEXTURE_2D,
          0,
          gl.RGBA,
          1,
          1,
          0,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          new Uint8Array([18, 30, 39, 255]),
        );
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      }
      const u = Object.fromEntries(
        UNIFORMS.map((k) => [k, gl.getUniformLocation(program, k)]),
      );
      ["texA", "texB", "prevA", "prevB"].forEach((k, unit) =>
        gl.uniform1i(u[k], unit),
      );
      const s = {
        frames: [
          { textures: resources.textures.slice(0, 2), images: {} },
          { textures: resources.textures.slice(2), images: {} },
        ],
        current: 0,
        fade: 1,
        raf: 0,
        calibration: null,
        yaw: 0,
        pitch: 0,
        fov: PANORAMA_FOV.initial,
      };
      s.draw = () => {
        if (!s.calibration) return;
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        const w = Math.max(1, Math.round(el.clientWidth * ratio)),
          h = Math.max(1, Math.round(el.clientHeight * ratio));
        // Resizing the drawing buffer is costly; only do it when the size changed.
        if (el.width !== w) el.width = w;
        if (el.height !== h) el.height = h;
        gl.viewport(0, 0, w, h);
        const now = s.frames[s.current],
          before = s.frames[1 - s.current];
        [...now.textures, ...before.textures].forEach((tex, unit) => {
          gl.activeTexture(gl.TEXTURE0 + unit);
          gl.bindTexture(gl.TEXTURE_2D, tex);
        });
        const size = (img) => [img?.naturalWidth || 800, img?.naturalHeight || 600];
        ["A", "B"].forEach((eye) => {
          const c = s.calibration.cameras[eye];
          gl.uniform2f(u[`size${eye}`], ...size(now.images[eye]));
          gl.uniform2f(u[`prevSize${eye}`], ...size(before.images[eye]));
          gl.uniform4f(
            u[`cal${eye}`],
            c.center_x,
            c.center_y,
            c.radius,
            c.fov_deg * 0.5 * DEG,
          );
          gl.uniform3f(
            u[`opt${eye}`],
            c.roll_deg * DEG,
            c.flip_x ? -1 : 1,
            c.flip_y ? -1 : 1,
          );
          gl.uniform1f(u[`yaw${eye}`], c.yaw_deg * DEG);
        });
        gl.uniform2f(u.has, now.images.A ? 1 : 0, now.images.B ? 1 : 0);
        gl.uniform2f(u.prevHas, before.images.A ? 1 : 0, before.images.B ? 1 : 0);
        gl.uniform1f(u.fade, s.fade);
        gl.uniform1f(u.yaw, s.yaw * DEG);
        gl.uniform1f(u.pitch, s.pitch * DEG);
        gl.uniform1f(u.fov, s.fov * DEG);
        gl.uniform1f(u.aspect, w / h);
        gl.uniform1f(u.seam, s.calibration.seam_deg * DEG);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      };
      // Upload a new capture into the spare texture pair and fade it in over the one on screen.
      s.show = (next) => {
        const shown = s.frames[s.current];
        if (shown.images.A === next.A && shown.images.B === next.B) return;
        const hadFrame = !!(shown.images.A || shown.images.B);
        s.current = 1 - s.current;
        const target = s.frames[s.current];
        target.images = { A: next.A, B: next.B };
        ["A", "B"].forEach((eye, i) => {
          if (!next[eye]) return;
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, target.textures[i]);
          gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
          gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            gl.RGBA,
            gl.RGBA,
            gl.UNSIGNED_BYTE,
            next[eye],
          );
        });
        cancelAnimationFrame(s.raf);
        if (!hadFrame || prefersReducedMotion()) {
          s.fade = 1;
          return;
        }
        s.fade = 0;
        const start = performance.now();
        const step = (time) => {
          s.fade = Math.min(1, Math.max(0, (time - start) / FRAME_FADE_MS));
          s.draw();
          if (s.fade < 1) s.raf = requestAnimationFrame(step);
        };
        s.raf = requestAnimationFrame(step);
      };
      state.current = s;
      resize = new ResizeObserver(() => s.draw());
      resize.observe(el);
      setError("");
    } catch (e) {
      setError(e.message);
    }
    return () => {
      resize?.disconnect();
      if (state.current) cancelAnimationFrame(state.current.raf);
      state.current = null;
      resources.textures.forEach((t) => gl.deleteTexture(t));
      resources.shaders.forEach((s) => gl.deleteShader(s));
      if (resources.program) gl.deleteProgram(resources.program);
      if (resources.buffer) gl.deleteBuffer(resources.buffer);
    };
  }, []);
  useEffect(() => {
    const s = state.current;
    if (!s) return;
    s.calibration = calibration;
    // Calibration previews only redraw; textures are uploaded once per new capture.
    s.show(images);
    s.draw();
  }, [images, calibration]);
  useEffect(() => {
    const s = state.current;
    if (s) {
      s.yaw = calibration.heading_deg;
      s.pitch = 0;
      s.fov = PANORAMA_FOV.initial;
      s.draw();
    }
  }, [resetKey, calibration.heading_deg]);
  // Wheel and pinch arrive through the page's non-passive listener (React's onWheel is passive).
  useImperativeHandle(
    viewRef,
    () => ({
      zoomBy(factor) {
        const s = state.current;
        if (!s) return;
        s.fov = zoomFov(s.fov, factor);
        s.draw();
      },
    }),
    [],
  );
  const drag = useRef(null);
  const move = (e) => {
    if (!drag.current || !state.current || e.pointerId !== drag.current.id)
      return;
    const s = state.current;
    s.yaw = drag.current.yaw - (e.clientX - drag.current.x) * 0.2;
    s.pitch = Math.max(
      -85,
      Math.min(85, drag.current.pitch + (e.clientY - drag.current.y) * 0.2),
    );
    s.draw();
  };
  const endDrag = (e) => {
    if (drag.current?.id === e.pointerId) drag.current = null;
  };
  return (
    <>
      <canvas
        ref={canvas}
        tabIndex={0}
        role="group"
        aria-describedby={describedBy}
        aria-label="Interactive panorama. Drag or press Shift and the arrow keys to look around; scroll, pinch or press plus and minus to zoom. Hatched areas have no camera coverage."
        onPointerDown={(e) => {
          if (!state.current || drag.current) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = {
            id: e.pointerId,
            x: e.clientX,
            y: e.clientY,
            yaw: state.current.yaw,
            pitch: state.current.pitch,
          };
        }}
        onPointerMove={move}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
        onKeyDown={(e) => {
          const s = state.current;
          if (!s || e.ctrlKey || e.metaKey || e.altKey) return;
          // Plain Left/Right step through captures (handled by the page), so turning is Shift+arrow.
          const actions = {
            ArrowLeft: e.shiftKey && (() => {
              s.yaw -= 5;
            }),
            ArrowRight: e.shiftKey && (() => {
              s.yaw += 5;
            }),
            ArrowUp: () => {
              s.pitch = Math.min(85, s.pitch + 5);
            },
            ArrowDown: () => {
              s.pitch = Math.max(-85, s.pitch - 5);
            },
            "+": () => {
              s.fov = clampFov(s.fov - 5);
            },
            "=": () => {
              s.fov = clampFov(s.fov - 5);
            },
            "-": () => {
              s.fov = clampFov(s.fov + 5);
            },
            _: () => {
              s.fov = clampFov(s.fov + 5);
            },
          };
          if (actions[e.key]) {
            e.preventDefault();
            actions[e.key]();
            s.draw();
          }
        }}
      />
      {error && (
        <div className="field-viewer-message" role="status">
          {error}
        </div>
      )}
    </>
  );
}
